import get from 'lodash/get'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  NavigateFunction,
  useNavigate,
  useParams,
  Outlet,
} from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { requireData } from '@disco/state-management/actions/AppActions'
import {
  addContactNote,
  cancelContactsEditing,
  changeContactValue,
  deleteContactNote,
  deleteContacts,
  loadContactActivity,
  loadContactNotes,
  updateContactNote,
  updateContacts,
  updateContactsTags,
} from '@disco/state-management/actions/contact-actions'
import {
  listFormStartManagingLists,
  loadEmailGroups,
} from '@disco/state-management/actions/EmailGroupActions'
import {
  getContactsSavingStatus,
  getProDropdownOptions,
  getPros,
  getPublisherDropdownOptions,
  getPublishers,
  makeGetContact,
} from '@disco/state-management/selectors/contacts'
import {
  getContactCustomFields,
  getHideContactRemovalSetting,
} from '@disco/state-management/selectors/user'

import type { Contact, CustomContactField } from 'types/list'
import type { State } from 'types/redux'
import type { Pro, Publisher } from 'types/writer'

import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import PageHeader from 'components/ui/page-header'
import PageSection from 'components/ui/page-section'
import UserAvatar from 'components/ui/user-avatar'

import ActivityTab from './activity-tab'
import DetailsTab from './details-tab'
import ListsTab from './lists-tab'
import NotesTab from './notes-tab'
import TagsTab from './tags-tab'
import type { RootState } from 'store/configureStore'

type DropdownOption = {
  value: string
  label: string
}

type OwnProps = {
  params: {
    contactId: string
  }
  initialTabIndex: number
  navigate: NavigateFunction
}

const mapStateToProps = (state: RootState, props: OwnProps) => {
  const getContact = makeGetContact()
  return {
    contact: getContact(state, {
      contactId: Number(props.params.contactId),
    }),
    emailGroups: state.emailGroups,
    proDropdownOptions: getProDropdownOptions(state),
    publisherDropdownOptions: getPublisherDropdownOptions(state),
    pros: getPros(state),
    publishers: getPublishers(state),
    saving: getContactsSavingStatus(state),
    hideContactRemoval: getHideContactRemovalSetting(state),
    customFields: getContactCustomFields(state),
  }
}

const mapDispatchToProps = {
  requireData,
  deleteContacts,
  updateContacts,
  changeContactValue,
  addContactNote,
  deleteContactNote,
  updateContactNote,
  listFormStartManagingLists,
  loadContactNotes,
  loadContactActivity,
  loadEmailGroups,
  cancelContactsEditing,
  updateContactsTags,
}

// Define connector
const connector = connect(mapStateToProps, mapDispatchToProps)

// Infer props type
type PropsFromRedux = ConnectedProps<typeof connector>

// Component props = Redux props + own props
type Props = PropsFromRedux & OwnProps

// Higher-order component to provide navigate function and params to class component
const withNavigate = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const navigate = useNavigate()
    const params = useParams<{ contactId: string }>()
    return <Component {...props} navigate={navigate} params={params} />
  }
}

class ContactDetails extends React.Component<
  Props,
  {
    detailsEditMode: boolean
  }
> {
  state = {
    detailsEditMode: false,
  }
  static defaultProps = {
    initialTabIndex: 0,
  }
  _switchToEditMode = () => {
    this.setState({
      detailsEditMode: true,
    })
  }
  _switchToViewMode = () => {
    this.setState({
      detailsEditMode: false,
    })
    this.props.cancelContactsEditing()
  }
  _onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.props.contact &&
    this.props.changeContactValue(
      this.props.contact.id,
      e.target.name,
      e.target.value,
    )
  _onCustomFieldChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.props.contact &&
    this.props.changeContactValue(this.props.contact.id, 'custom_data', {
      ...get(
        this.props.contact,
        'pendingChanges.custom_data',
        this.props.contact.custom_data || {},
      ),
      [e.target.name]: e.target.value,
    })
  _onProSelect = (pros: DropdownOption[]) => {
    const { changeContactValue, contact } = this.props
    if (!contact) return
    const proValues = pros.map(p => p.value.trim())
    changeContactValue(contact.id, 'pros', proValues)
  }
  _onPublisherSelect = (publishers: DropdownOption[]) => {
    const { changeContactValue, contact } = this.props
    if (!contact) return
    const publisherValues = publishers.map(p => p.value.trim())
    changeContactValue(contact.id, 'publishers', publisherValues)
  }
  _onDefaultProSelect = (value: string) => {
    const pro = this.props.pros.find(p => p.name === value.trim())

    if (pro) {
      this._updateContact(null, {
        default_pro: pro.id,
      })
    }
  }
  _onDefaultPublisherSelect = (value: string) => {
    const publisher = this.props.publishers.find(p => p.name === value.trim())

    if (publisher) {
      this._updateContact(null, {
        default_publisher: publisher.id,
      })
    }
  }
  _updateContact = (_: any, pendingChanges: any) => {
    if (this.props.contact) {
      ;(
        this.props.updateContacts(
          [this.props.contact],
          pendingChanges,
        ) as Promise<any>
      )
        .then(() => {
          mixpanel.sendPureEvent('Edit contact inside single contact view')

          this._switchToViewMode()
        })
        .catch(() => {
          this._switchToViewMode()
        })
    }
  }
  _onActionClick = (action: 'Delete' | 'Edit' | 'ChangeLists') => {
    const { deleteContacts, contact, hideContactRemoval, navigate } = this.props
    if (!contact) return

    switch (action) {
      case 'Delete':
        if (hideContactRemoval) {
          deleteContacts([contact.id])
          return navigate('/contacts')
        } else {
          return navigate(`/contacts/${this.props.params.contactId}/delete`)
        }

      case 'ChangeLists':
        this.props.listFormStartManagingLists([contact], 'MULTIPLE_LISTS')
        break

      default:
        throw new Error('Not implemented')
    }
  }
  _loadContactDetails = (contact: Contact | null | undefined) => {
    if (contact && !contact.notes) {
      this.props.loadContactNotes(contact.id)
    }

    if (contact) {
      this.props.loadContactActivity(contact.id)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const currentContactId = get(this.props.contact, 'id', null)
    const nextContactId = get(nextProps.contact, 'id', null)

    if (nextContactId && currentContactId !== nextContactId) {
      this._loadContactDetails(nextProps.contact)
    }

    if (
      nextProps.contact &&
      this._isActivityTabHidden(nextProps.contact) &&
      this.props.initialTabIndex === 1
    ) {
      this.props.navigate(`/contacts/${nextProps.contact.id}`)
    }
  }

  componentDidMount() {
    this.props.requireData({
      contacts: [this.props.params.contactId],
    })

    this._loadContactDetails(this.props.contact)

    this.props.loadEmailGroups()
    mixpanel.sendPureEvent('View single contact')
  }

  _menuOptions() {
    if (!this.props.contact) return []
    const nonListContact = !this.props.contact.email
    let changeListItem = {
      value: 'ChangeLists',
      label: <span>Add or remove from lists</span>,
      disabled: false,
    }

    if (nonListContact) {
      changeListItem.label = (
        <span className="ellipsis-menu__button--disabled">
          Add or remove from lists
          <HelpTooltip>
            Contact without email could not be added to list.
          </HelpTooltip>
        </span>
      )
      changeListItem.disabled = true
    }

    return [
      changeListItem,
      {
        value: 'Delete',
        label: 'Delete',
        destructive: true,
      },
    ]
  }

  _isActivityTabHidden(contact: Contact): boolean {
    const { is_team_member, referenced_business } = contact
    if (referenced_business) return true
    if (is_team_member) return true
    return false
  }

  render() {
    const { contact, changeContactValue, updateContactsTags, customFields } =
      this.props
    const { detailsEditMode } = this.state

    if (!contact) {
      return null
    }

    return (
      <React.Fragment>
        <DocumentHeadTags title="Contact details | DISCO" />
        <PageHeader
          breadcrumbs={{
            content: 'Back to contacts',
            link: '/contacts/list',
          }}
          title={contact.name || 'Unnamed contact'}
          titleBefore={<UserAvatar name={contact.name} />}
          actions={
            <EllipsisMenu
              compact
              caption="Actions"
              options={this._menuOptions()}
              onOptionClick={this._onActionClick}
            />
          }
        />
        <PageSection
          title="Details" // description="We can use some help text here that can go into two lines"
        >
          <DetailsTab
            editMode={detailsEditMode}
            contact={contact}
            customFields={customFields}
            onUpdate={this._updateContact}
            onCancel={this._switchToViewMode}
            onFieldChange={this._onFieldChange}
            onCustomFieldChange={this._onCustomFieldChange}
            onProSelect={this._onProSelect}
            onPublisherSelect={this._onPublisherSelect}
            onDefaultProSelect={this._onDefaultProSelect}
            onDefaultPublisherSelect={this._onDefaultPublisherSelect}
            onSwitchToEditMode={this._switchToEditMode}
            pros={this.props.pros}
            publishers={this.props.publishers}
            proDropdownOptions={this.props.proDropdownOptions}
            publisherDropdownOptions={this.props.publisherDropdownOptions}
            saving={this.props.saving}
          />
        </PageSection>
        <PageSection
          title="Tags" // description="We can use some help text here that can go into two lines"
        >
          <TagsTab
            contact={contact}
            changeContactValue={changeContactValue}
            updateContactsTags={updateContactsTags}
          />
        </PageSection>
        <PageSection
          title="Notes" // description="We can use some help text here that can go into two lines"
        >
          <NotesTab
            contact={contact}
            addContactNote={this.props.addContactNote}
            deleteContactNote={this.props.deleteContactNote}
            updateContactNote={this.props.updateContactNote}
          />
        </PageSection>
        {contact.email && (
          <PageSection
            title="Mailing Lists" // description="We can use some help text here that can go into two lines"
          >
            <ListsTab
              contact={contact}
              emailGroups={this.props.emailGroups}
              listFormStartManagingLists={this.props.listFormStartManagingLists}
            />
          </PageSection>
        )}
        {/*
         https://app.asana.com/0/364097350002545/402398786337192/f we haven't
         figured activity for business inbox contacts yet
        */}
        {!this._isActivityTabHidden(contact) && (
          <PageSection
            title="Activity"
            description={
              contact.activity &&
              contact.activity.length > 5 && (
                <Button
                  appearance="inline-link"
                  caption="Click here to view all "
                  link={`/contacts/${contact.id}/activity`}
                />
              )
            }
          >
            <ActivityTab contact={contact} />
          </PageSection>
        )}
        <Outlet />
      </React.Fragment>
    )
  }
}

const ConnectedContactDetails = connector(ContactDetails)
export default withNavigate(ConnectedContactDetails)
